import React, { useEffect, useState } from "react"
import { makeStyles, Theme } from "@material-ui/core/styles"
import { Outlet, useLocation, useNavigate } from "react-router-dom"

import { logger } from "util/logger"
import useAppState from "hooksV1/useAppState"
import { generateClient } from "aws-amplify/api"
import useBooleanState from "hooksV1/useBooleanStates"
import SideNaveV1 from "components/SideNav/SideNaveV1"
import { listDocumentsByCountryId } from "graphql/queries"
import { containsPath, sortDocuments } from "util/helper"
import DocumentsToolBar from "components/DocumentsToolBar"
import useSnackBar, { SnackType } from "hooksV1/useSnackBar"
import ManageDocumentsToolBar from "components/ManageDocumentsToolBar/ManageDocumentsToolBar"
import { Document } from "shared/types-exp"

const useStyles = makeStyles((theme: Theme) => ({
  main: {
    flexGrow: 1,
    padding: "0.75rem",
  },
  root: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  container: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "row",
    marginTop: "0.5rem",
  },
  sideNav: {
    width: "12.5%",
    height: "100%",
  },
  body: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
  },
}))

const client = generateClient()

const TemplateDocumentV1: React.FC = () => {
  const {
    activeCountry,
    activeDocument,
    setDocuments,
    setActiveDocument,
    setActiveDocumentVersion,
  } = useAppState()
  const classes = useStyles()
  let navigate = useNavigate()
  const location = useLocation()

  const snackBar = useSnackBar()
  const booleanState = useBooleanState()

  const pathArray = location.pathname.split("/")
  const [currentPath, setCurrentPath] = useState<string[]>([])

  useEffect(() => {
    if (!activeCountry || !hasCountryOrDocumentChanged()) return

    fetchCountryData()
  }, [location, activeCountry])

  useEffect(() => {
    localStorage.setItem("chapterIndex", "1.")
  }, [])

  const fetchCountryData = async () => {
    try {
      booleanState.setIsDocumentVersionLoading(true)

      // 1. Fetch documents
      const documents: any = await fetchEnabledDocuments(activeCountry?.id)

      // 2. Process and set documents
      const processedDocs = processDocuments(documents, activeDocument)

      // 3. Handle navigation if needed
      handleDocumentNavigation(processedDocs)

      return processedDocs
    } catch (error) {
      handleFetchError(error)
    } finally {
      cleanup()
    }
  }

  // Helper functions for separation of concerns
  const fetchEnabledDocuments = async (countryId?: string) => {
    const result = await client.graphql({
      query: listDocumentsByCountryId,
      variables: {
        filter: {
          countryId: { eq: countryId },
          enabled: { eq: true },
        },
      },
    })

    if (result.errors) {
      const errorMessages = result.errors.map((e) => e.message).join(", ")
      throw new Error(`GraphQL Error: ${errorMessages}`)
    }

    return result?.data?.listDocuments?.items ?? []
  }

  const processDocuments = (
    documents: any[],
    activeDocument?: { refId: string }
  ) => {
    const sortedDocs = sortDocuments(documents)
    const activeDocumentId = pathArray[3]

    // Find active document by ID or fallback to refId
    const activeDoc =
      sortedDocs.find((doc) => doc.id === activeDocumentId) ||
      sortedDocs.find((doc) => doc.refId === activeDocument?.refId)

    // Update state
    setDocuments(sortedDocs)

    if (activeDoc) {
      setActiveDocument(activeDoc)
      setActiveDocumentVersion(activeDoc.documentVersions["items"][0])
    }

    return {
      activeDocument: activeDoc,
    }
  }

  const handleDocumentNavigation = ({
    activeDocument,
  }: {
    activeDocument?: Document
  }) => {
    if (!activeDocument) return

    const activeCountryName = pathArray[5]?.replace(/%20/g, " ")
    const currentPath = pathArray[pathArray.length - 1]
    const basePath = `/template/document/${activeDocument.id}/country/${activeCountryName}`

    if (!containsPath(currentPath)) {
      navigate(basePath)
    } else {
      navigate(`${basePath}/${currentPath}`)
    }
  }

  const handleFetchError = (error: unknown) => {
    logger("TemplateDocumentV1", "useEffect (fetchCountryData)", error)

    snackBar.setMessage(
      "An error occurred fetching the countries information. Please try again."
    )
    snackBar.setMessageSeverity(SnackType.SnackError)
    snackBar.onOpen()
  }

  const cleanup = () => {
    setCurrentPath(pathArray)
    booleanState.setIsDocumentVersionLoading(false)
  }

  const hasCountryOrDocumentChanged = (): boolean => {
    const pathArray = location.pathname.split("/")

    return pathArray[3] !== currentPath[3] || pathArray[5] !== currentPath[5]
  }

  return (
    <main className={classes.main}>
      <div className={classes.root}>
        <DocumentsToolBar />
        <div className={classes.container}>
          <div className={classes.sideNav}>
            <SideNaveV1 isTemplateDocuments />
          </div>
          <div className={classes.body}>
            <div
              style={{
                paddingLeft: "0.5rem",
              }}
            >
              <ManageDocumentsToolBar />
            </div>
            <Outlet />
          </div>
        </div>
      </div>
    </main>
  )
}

export default TemplateDocumentV1
